import { axiosInstance } from '../../utils/axios';
import type {
  PartnerDeviceListResponse,
  PartnerDeviceWithAssignmentListResponse,
  ListPartnerDevicesParams,
  ClaimRicRequest,
  ClaimRicResponse,
  PartnerUserListResponse,
  PartnerCustomerListResponse,
  PartnerCustomerLookupResponse,
  PartnerCustomerDetail,
  PartnerPendingCustomerDetail,
  InviteCustomerRequest,
  InviteCustomerResponse,
  AddDevicesRequest,
  AddDevicesResponse,
  DeviceRegistrationRequest,
  DeviceRegistrationResponse,
  ResendInvitationResponse,
} from '../../types/partner';

const BASE_URL = '/partner';

/**
 * Get Partner API Key from localStorage
 * Partner API uses API key authentication (Bearer token), not JWT
 */
function getPartnerApiKey(): string | null {
  const apiKey = localStorage.getItem('partner_api_key');
  if (!apiKey || apiKey === 'undefined' || apiKey === 'null') {
    return null;
  }
  return apiKey;
}

/**
 * Partner endpoints wrap responses in `{ success, data, error }`. This helper
 * unwraps the envelope and throws a meaningful error on `success: false`.
 */
type PartnerEnvelope<T> = {
  success: boolean
  data?: T
  error?: { code?: string; message?: string }
  message?: string
}

function unwrap<T>(envelope: PartnerEnvelope<T>): T {
  if (!envelope || envelope.success === false) {
    const message =
      envelope?.error?.message || envelope?.message || 'Partner API request failed'
    const err = new Error(message) as Error & { code?: string }
    err.code = envelope?.error?.code
    throw err
  }
  return (envelope.data ?? ({} as T)) as T
}

/**
 * API Client wrapper for partner operations.
 *
 * Auth resolution:
 *   - If a partner API key is in localStorage, send it as Bearer (legacy /
 *     external-integration path).
 *   - Otherwise, let axios's request interceptor inject the user's Supabase
 *     JWT. The backend's `validatePartnerSession` middleware accepts either
 *     and gates JWTs on USERROLE_ID = 2 (PARTNER).
 *
 * This means a logged-in partner-role user can hit these endpoints without
 * pasting an API key into localStorage.
 */
function authHeaderOverride(): Record<string, string> | undefined {
  const apiKey = getPartnerApiKey();
  return apiKey ? { Authorization: `Bearer ${apiKey}` } : undefined;
}

const apiClient = {
  get: async <T>(url: string, params?: Record<string, string>): Promise<T> => {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const headers = authHeaderOverride();
    const response = await axiosInstance.get<PartnerEnvelope<T>>(
      `${url}${queryString}`,
      headers ? { headers } : undefined
    );
    return unwrap(response.data);
  },

  post: async <T>(url: string, data?: unknown): Promise<T> => {
    const headers = authHeaderOverride();
    const response = await axiosInstance.post<PartnerEnvelope<T>>(
      url,
      data,
      headers ? { headers } : undefined
    );
    return unwrap(response.data);
  },

  put: async <T>(url: string, data?: unknown): Promise<T> => {
    const headers = authHeaderOverride();
    const response = await axiosInstance.put<PartnerEnvelope<T>>(
      url,
      data,
      headers ? { headers } : undefined
    );
    return unwrap(response.data);
  },

  delete: async <T>(url: string): Promise<T> => {
    const headers = authHeaderOverride();
    const response = await axiosInstance.delete<PartnerEnvelope<T>>(
      url,
      headers ? { headers } : undefined
    );
    return unwrap(response.data);
  },
};

// ============================================================================
// Device Registration API
// ============================================================================

export const deviceRegistrationApi = {
  /**
   * Register a device to a customer
   * Creates a registration token and sends email invitation
   *
   * @param email - Customer email address
   * @param deviceId - Device ID to register
   * @returns Registration response with token and pending account info
   */
  registerDevice: async (
    email: string,
    deviceId: number
  ): Promise<DeviceRegistrationResponse> => {
    const data: DeviceRegistrationRequest = { email, deviceId };
    return apiClient.post<DeviceRegistrationResponse>(
      `${BASE_URL}/register-device`,
      data
    );
  },
};

// ============================================================================
// Partner Device API
// ============================================================================

export const partnerDeviceApi = {
  /**
   * List all devices for this partner (legacy shape).
   */
  listDevices: async (
    page = 1,
    limit = 10
  ): Promise<PartnerDeviceListResponse> => {
    const params = {
      page: page.toString(),
      limit: limit.toString(),
    };

    return apiClient.get<PartnerDeviceListResponse>(
      `${BASE_URL}/devices`,
      params
    );
  },

  /**
   * List partner-owned RICs with current customer/group assignment.
   * Backed by GET /api/partner/devices/inventory.
   */
  list: async (
    options: ListPartnerDevicesParams = {}
  ): Promise<PartnerDeviceWithAssignmentListResponse> => {
    const params: Record<string, string> = {};
    if (options.search) params.search = options.search;
    if (options.assignment && options.assignment !== 'all') {
      params.assignment = options.assignment;
    }
    if (options.status) params.status = options.status;

    return apiClient.get<PartnerDeviceWithAssignmentListResponse>(
      `${BASE_URL}/devices/inventory`,
      params
    );
  },

  /**
   * Claim a brand-new RIC into the partner's inventory.
   * Self-assigns PARTNER_USER_ID; LOCATION_ID stays unassigned (0).
   */
  claim: async (payload: ClaimRicRequest): Promise<ClaimRicResponse> => {
    return apiClient.post<ClaimRicResponse>(`${BASE_URL}/devices/claim`, payload);
  },
};

// ============================================================================
// Partner User/Customer API
// ============================================================================

export const partnerUserApi = {
  /**
   * List all users/customers for this partner
   *
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @param status - Filter by status: 'all', 'active', or 'pending' (default: 'all')
   * @returns User list with pagination
   */
  listUsers: async (
    page = 1,
    limit = 10,
    status: 'all' | 'active' | 'pending' = 'all'
  ): Promise<PartnerUserListResponse> => {
    const params = {
      page: page.toString(),
      limit: limit.toString(),
      status,
    };

    return apiClient.get<PartnerUserListResponse>(
      `${BASE_URL}/users`,
      params
    );
  },

  /**
   * Resend invitation email to a pending user
   *
   * @param userId - User ID
   * @returns Response with new registration token
   */
  resendInvitation: async (userId: string): Promise<ResendInvitationResponse> => {
    return apiClient.post<ResendInvitationResponse>(
      `${BASE_URL}/users/${userId}/resend-invitation`
    );
  },
};

// ============================================================================
// Partner Customer API (invite, add devices, lookup, password reset)
// ============================================================================

export const partnerCustomerApi = {
  list: async (
    status: 'all' | 'active' | 'pending' = 'all'
  ): Promise<PartnerCustomerListResponse> => {
    const params: Record<string, string> = {};
    if (status !== 'all') params.status = status;
    return apiClient.get<PartnerCustomerListResponse>(
      `${BASE_URL}/customers`,
      params
    );
  },

  get: async (userId: number): Promise<PartnerCustomerDetail> => {
    return apiClient.get<PartnerCustomerDetail>(
      `${BASE_URL}/customers/${userId}`
    );
  },

  /**
   * Look up an email to detect an existing CUSTOMER and morph the invite form
   * into "add devices" mode.
   */
  lookup: async (email: string): Promise<PartnerCustomerLookupResponse> => {
    return apiClient.post<PartnerCustomerLookupResponse>(
      `${BASE_URL}/customers/lookup`,
      { email }
    );
  },

  invite: async (
    payload: InviteCustomerRequest
  ): Promise<InviteCustomerResponse> => {
    return apiClient.post<InviteCustomerResponse>(
      `${BASE_URL}/customers/invite`,
      payload
    );
  },

  addDevices: async (
    userId: number,
    payload: AddDevicesRequest
  ): Promise<AddDevicesResponse> => {
    return apiClient.post<AddDevicesResponse>(
      `${BASE_URL}/customers/${userId}/add-devices`,
      payload
    );
  },

  unassignDevice: async (
    userId: number,
    deviceId: number
  ): Promise<{ deviceId: number; wisolId: string; previousLocationId: number }> => {
    return apiClient.post<{ deviceId: number; wisolId: string; previousLocationId: number }>(
      `${BASE_URL}/customers/${userId}/devices/${deviceId}/unassign`
    );
  },

  sendPasswordReset: async (userId: number): Promise<{ ok: true }> => {
    return apiClient.post<{ ok: true }>(
      `${BASE_URL}/customers/${userId}/send-password-reset`
    );
  },

  cancelInvite: async (pendingAccountId: string): Promise<{ ok: true }> => {
    return apiClient.post<{ ok: true }>(
      `${BASE_URL}/customers/${pendingAccountId}/cancel-invite`
    );
  },

  getPending: async (pendingAccountId: string): Promise<PartnerPendingCustomerDetail> => {
    return apiClient.get<PartnerPendingCustomerDetail>(
      `${BASE_URL}/pending-accounts/${pendingAccountId}`
    );
  },

  regenerateRegistrationLink: async (
    pendingAccountId: string
  ): Promise<{ registrationUrl: string; tokenExpiresAt: string; expiresAt: string }> => {
    return apiClient.post<{ registrationUrl: string; tokenExpiresAt: string; expiresAt: string }>(
      `${BASE_URL}/pending-accounts/${pendingAccountId}/regenerate-link`
    );
  },
};

// ============================================================================
// Partner API Key Management (Admin only)
// ============================================================================


// ============================================================================
// Partner Session Probe
// ============================================================================

/**
 * GET /api/partner/session — confirms the current credential (API key or
 * Supabase JWT for a PARTNER tuser) is accepted by the backend. Returns the
 * partner's tuser USER_ID so the frontend can scope subsequent UI to it.
 */
export const partnerSessionApi = {
  check: async (): Promise<{ partnerId: number | null; authMode: 'session' | 'apiKey' }> => {
    return apiClient.get<{ partnerId: number | null; authMode: 'session' | 'apiKey' }>(
      `${BASE_URL}/session`
    );
  },
};

// ============================================================================
// Combined Partner API Export
// ============================================================================

export const partnerApi = {
  // Device registration (legacy single-RIC flow, retained for backward compat)
  registerDevice: deviceRegistrationApi.registerDevice,

  // Device management
  listDevices: partnerDeviceApi.listDevices,
  listDevicesWithAssignment: partnerDeviceApi.list,
  claimDevice: partnerDeviceApi.claim,

  // User/customer management (legacy)
  listUsers: partnerUserApi.listUsers,
  resendInvitation: partnerUserApi.resendInvitation,

  // Customer invite + assignment (new partner portal flow)
  listCustomers: partnerCustomerApi.list,
  getCustomer: partnerCustomerApi.get,
  lookupCustomer: partnerCustomerApi.lookup,
  inviteCustomer: partnerCustomerApi.invite,
  addDevicesToCustomer: partnerCustomerApi.addDevices,
  sendPasswordReset: partnerCustomerApi.sendPasswordReset,
  unassignDeviceFromCustomer: partnerCustomerApi.unassignDevice,
  cancelInvite: partnerCustomerApi.cancelInvite,
  getPendingCustomer: partnerCustomerApi.getPending,
  regenerateRegistrationLink: partnerCustomerApi.regenerateRegistrationLink,

  // Session probe (works with either API key or Supabase JWT for PARTNER role)
  checkSession: partnerSessionApi.check,


  // Utility functions
  getPartnerApiKey,
  setPartnerApiKey: (apiKey: string) => {
    localStorage.setItem('partner_api_key', apiKey);
  },
  clearPartnerApiKey: () => {
    localStorage.removeItem('partner_api_key');
  },
};
